import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import {Subject} from 'rxjs';
import * as routes from '@app/routes';
import {select, Store} from '@ngrx/store';
import {
  getRegistrationPlatformError,
  getRegistrationPlatformPaginated,
  getRegistrationPlatformSuccess,
  IAuthenticationState,
  IRegistrationPlatformState,
  RegistrationPlatformDelete,
  RegistrationPlatformListPaginated,
  ResetAuthState,
  ResetRegistrationPlatformState,
} from '@app/stores';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {Constants} from '@app/consts';

import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { EntityLinkDirective } from '../../../_components/unified/entity-form-modal/entity-link.directive';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { SbTableComponent } from '@app/_components/unified/sb-table/sb-table.component';
import {
  SbTableActionsDirective, SbTableCellDirective, SbTableFilterExtraDirective, SbTableHeaderDirective
} from '@app/_components/unified/sb-table/sb-table.directives';
import { SbTableColumn, SbTableOptions, SbTableServerState } from '@app/_components/unified/sb-table/sb-table.types';
@Component({
  selector: 'app-registration-platform-list',
  templateUrl: './registration-platform-list.component.html',
  styleUrls: ['./registration-platform-list.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, EntityLinkDirective, RouterLink, CreatableSelectComponent, SbTableComponent, SbTableCellDirective,
    SbTableHeaderDirective, SbTableActionsDirective, SbTableFilterExtraDirective, MatMenuTrigger, MatMenu]
})
export class RegistrationPlatformListComponent implements OnInit, OnDestroy {
  private registrationPlatformStore = inject<Store<IRegistrationPlatformState>>(Store);
  private authStore = inject<Store<IAuthenticationState>>(Store);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  constants = inject(Constants);
  private toastrService = inject(ToastrService);


  unsubscriber = new Subject();
  routes = routes;
  readonly registrationPlatformList = signal<any[]>([]);
  readonly totalPlatforms = signal(0);

  // The list does not sort, so the ids are only names for the cell templates.
  readonly tableColumns: SbTableColumn[] = [
    {id: 'image', header: ''},
    {id: 'name', header: 'Name'},
    {id: 'website', header: 'Website'},
    {id: 'widget_projects', header: 'Widget projects', width: '260px'},
    {id: 'integration_projects', header: 'Sync projects', width: '260px'},
    {id: 'widget_integration_guide', header: 'Widget Integration Guide'},
    {id: 'widget_customization_code', header: 'Widget Custom Code'},
    {id: 'widget_customization_css', header: 'Widget Custom CSS'},
  ];
  readonly tableOptions: SbTableOptions = {
    serverSide: true,
    urlState: true,
    ordering: false,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    searchLabel: 'Search By Name:',
    selectable: false,
  };
  deleteAction = false;
  updateAction = false;
  loginWithUser = false;
  supportType = null;
  valuesType = null;
  widgetFilterValues = null;
  syncFilterValues = null;
  platformIdFilter: number = null;
  supportTypes = [
    {name: 'True', value: true},
    {name: 'False', value: false}
  ];
  supportTypeFilterValue = {};

  widgetSupportValues = [
    {name: 'Minimum (Clients might need help and it may not always work)', value: 'basic'},
    {name: 'Full (Clients can easily add without any help and works but there are workarounds)', value: 'full'},
    {name: 'Ultimate (Clients can easily add without any help and works reliably)', value: 'ultimate'}
  ];

  syncSupportValues = [
    {name: 'Basic (Limited Categories)', value: 'basic'},
    {name: 'Full (All Categories)', value: 'full'}
  ];

  constructor() {
    this.registrationPlatformStore.dispatch(ResetRegistrationPlatformState({params: {error: '', paginatedRegistrationPlatform: null}}));
    this.authStore.dispatch(ResetAuthState({params: {error: '', success: ''}}));
    this.subscribeStores();
  }

  subscribeStores() {
    this.registrationPlatformStore.pipe(select(getRegistrationPlatformError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error, 'Error');
        }
      });

    this.registrationPlatformStore.pipe(select(getRegistrationPlatformSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.deleteAction) {
            this.deleteAction = false;
            this.loadAllPlatforms();
          }
        }
      });

    this.registrationPlatformStore.pipe(select(getRegistrationPlatformPaginated))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((paginatedRegistrationPlatform: any) => {
        if (paginatedRegistrationPlatform) {
          this.registrationPlatformList.set(paginatedRegistrationPlatform.list);
          this.totalPlatforms.set(paginatedRegistrationPlatform.paging.total);
        }
      }
      );
  }

  ngOnInit() {
    this.applyQueryParamFilters();
  }

  /** The table asks for a page: the request the list has always sent for it. */
  onTableState(state: SbTableServerState) {
    this.loadAllPlatforms(state.page - 1, state.pageLength, state.search);
  }

  private applyQueryParamFilters() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    if (queryParams.platform_id) {
      this.platformIdFilter = Number(queryParams.platform_id) || null;
    }
  }

  private loadAllPlatforms(page = 0, perPage = 10, search = '', support_type = {}) {
    const options = {includePagination: true, includeProjects: true};
    if (this.platformIdFilter) {
      options['platform_id'] = this.platformIdFilter;
    }
    if (this.supportTypeFilterValue && Object.keys(this.supportTypeFilterValue)?.length) {
      Object.keys(this.supportTypeFilterValue).forEach(key => {
        support_type[key] = this.supportTypeFilterValue[key];
      });
    }
    if (this.syncFilterValues) {
      support_type['sync_support'] = this.syncFilterValues;
    }
    if (this.widgetFilterValues) {
      support_type['widget_support'] = this.widgetFilterValues;
    }
    Object.assign(options, support_type);
    console.log(options);
    this.registrationPlatformStore.dispatch(RegistrationPlatformListPaginated({
      params: {
        options: JSON.stringify(options),
        page: page + 1,
        perPage,
        search
      }
    }));
  }

  onSupportSelect(event = null, key) {
    if (event) {
      this.supportTypeFilterValue[key] = event.value;
    } else {
      delete this.supportTypeFilterValue[key];
    }
    this.loadAllPlatforms(0, 10);
  }

  onWidgetValuesSelect(event = null) {
    this.widgetFilterValues = null;
    if (event) {
      this.widgetFilterValues = event.value;
    }
    this.loadAllPlatforms(0, 10);
  }
  onSyncValuesSelect(event = null) {
    this.syncFilterValues = null;
    if (event) {
      this.syncFilterValues = event.value;
    }
    this.loadAllPlatforms(0, 10);
  }
  navigateAssociates(projectId) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/${routes.campaigns({projectId})}`])
    );
    window.open(url, '_blank');
  }

  deleteRegistrationPlatform(registrationPlatformId: number) {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.value) {
        this.deleteAction = true;
        this.registrationPlatformStore.dispatch(RegistrationPlatformDelete({registrationPlatformId}));
      }
    });
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
